"use client";

import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import Icon, {ICON_ANIMATE, type IconNameExtended} from "@/components/library/Icon";
import {Collapsible, CollapsibleContent, CollapsibleTrigger} from "@/components/ui/collapsible";
import {useSyncCollapsibleOpen} from "@/hooks/useSyncCollapsibleOpen";
import {cn} from "@/lib/utils";
import CodeExecutionToolStep from "./CodeExecutionToolStep";
import {
   type ToolCallStep,
   type CodeExecutionStepDetail,
   type ExecutionState,
   type TranslateFn,
   buildCodeExecutionStep,
   getCodeExecutionKind,
   getExecutionState,
} from "./toolCallUtils";

const FAILED_LABEL: Record<ReturnType<typeof getCodeExecutionKind>, string> = {
   python: "tools.phrases.pythonFailed",
   shell: "tools.phrases.shellCommandFailed",
   code: "tools.phrases.codeFailed",
};

const RUNNING_LABEL: Record<ReturnType<typeof getCodeExecutionKind>, string> = {
   python: "tools.phrases.runningPython",
   shell: "tools.phrases.runningShellCommand",
   code: "tools.phrases.runningCode",
};

const getSingleStepLabel = (
   step: ToolCallStep,
   detail: CodeExecutionStepDetail,
   isCompleted: boolean,
   t: TranslateFn
): string => {
   const kind = getCodeExecutionKind(step.data.functionName ?? "", step.data.parameters);
   if (detail.failed) return t(FAILED_LABEL[kind]);
   if (isCompleted) return detail.title;
   return t(RUNNING_LABEL[kind]);
};

const multiStepLabels: Record<string, {done: string; active: string}> = {
   shell: {done: "tools.phrases.ranCommands", active: "tools.phrases.runningCommands"},
   python: {done: "tools.phrases.ranScripts", active: "tools.phrases.runningScripts"},
   code: {done: "tools.phrases.ranSteps", active: "tools.phrases.runningSteps"},
   mixed: {done: "tools.phrases.ranSteps", active: "tools.phrases.runningSteps"},
};

const getCodeExecutionGroupLabel = (
   steps: ToolCallStep[],
   stepDetails: CodeExecutionStepDetail[],
   isCompleted: boolean,
   t: TranslateFn
): string => {
   if (steps.length === 1) {
      return getSingleStepLabel(steps[0], stepDetails[0], isCompleted, t);
   }

   const count = steps.length;
   const kinds = new Set(
      steps.map((step) => getCodeExecutionKind(step.data.functionName ?? "", step.data.parameters))
   );
   const category = kinds.size === 1 ? [...kinds][0] : "mixed";
   const keys = multiStepLabels[category];
   return t(isCompleted ? keys.done : keys.active, {count});
};

const SUMMARY_PRESENTATION: Record<
   ExecutionState,
   {iconName: IconNameExtended; iconClass: string; labelClass: string}
> = {
   failed: {
      iconName: "triangle-exclamation",
      iconClass: "text-destructive",
      labelClass: "text-destructive",
   },
   completed: {
      iconName: "terminal",
      iconClass: "text-400",
      labelClass: "text-500 group-hover:text-700",
   },
   running: {
      iconName: "spinner",
      iconClass: "text-gpt-accent",
      labelClass: "font-medium text-600 animate-text-shimmer",
   },
};

export default function CodeExecutionGroup({
   steps,
   isCompleted,
}: Readonly<{
   steps: ToolCallStep[];
   isCompleted: boolean;
}>) {
   const {t} = useTranslation("ask");

   const stepDetails = useMemo(
      () => steps.map((step) => buildCodeExecutionStep(step, t)),
      [steps, t]
   );
   const summaryLabel = getCodeExecutionGroupLabel(steps, stepDetails, isCompleted, t);
   const hasFailure = stepDetails.some((step) => step.failed);
   const singleStep = stepDetails.length === 1;
   // Live runs and failures stay open; successful completions collapse.
   const [open, setOpen] = useSyncCollapsibleOpen(!isCompleted || hasFailure);
   const summary = SUMMARY_PRESENTATION[getExecutionState(hasFailure, isCompleted)];

   return (
      <div className="font-sans">
         <Collapsible open={open} onOpenChange={setOpen}>
            <CollapsibleTrigger
               className="group -ml-1.5 flex min-h-7 max-w-full items-center gap-2 rounded-md px-1.5 text-left transition-colors hover:bg-neutral/30"
            >
               <span
                  title={summaryLabel}
                  className={cn(
                     "flex h-5 w-5 flex-shrink-0 items-center justify-center",
                     summary.iconClass
                  )}
               >
                  <Icon
                     icon={summary.iconName}
                     animate={summary.iconName === "spinner" ? ICON_ANIMATE.SPIN : undefined}
                     className="h-3 w-3"
                  />
               </span>
               <span
                  className={cn(
                     "min-w-0 truncate text-[13px] leading-5 transition-colors",
                     summary.labelClass
                  )}
               >
                  {summaryLabel}
               </span>
               <Icon
                  icon="chevron-right"
                  className={cn(
                     "h-2.5 w-2.5 flex-shrink-0 text-300 transition-transform duration-150",
                     open && "rotate-90"
                  )}
               />
            </CollapsibleTrigger>

            <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0">
               <div className="ml-2.5 mt-1.5 border-l border-neutral/70 pl-3">
                  {stepDetails.map((step) => (
                     <CodeExecutionToolStep
                        key={step.id}
                        detail={step}
                        isCompleted={isCompleted}
                        contentOnly={singleStep}
                     />
                  ))}
               </div>
            </CollapsibleContent>
         </Collapsible>
      </div>
   );
}
